import type { JournalListItem } from '~/utils/solr/journal'
import { SolrParamsKey } from '#shared/solr'
import { createCountWithFilters } from '~/utils/filter-count'
import { extractLabel } from '#shared/utils/solr-facet'
import { compareLocaleNorwegian } from '~/utils/string'

type JournalWithYearRange = JournalListItem & {
  yearRange: {
    start: number
    end: number
  }
}
interface JournalFilterState {
  searchKeyword: string
  sortFilter: { value: string, label: string }
  selectedLegalArea: { value: string, label: string }[]
}

type JournalDrawerOverrides = Omit<JournalFilterState, 'searchKeyword' | 'sortFilter'>

export function journalMatches(journal: JournalListItem, state: JournalFilterState): boolean {
  if (!matchesAllSearchTerms(state.searchKeyword, [journal.title || '', ...(journal.publisher || [])])) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const journalAreas = new Set(journal.legal_areas ?? [])
    if (!state.selectedLegalArea.some(area => journalAreas.has(area.value))) {
      return false
    }
  }

  return true
}

export function _useJournalFilters() {
  const searchKeyword = useState<string>('journal-filter-search', () => '')
  const sortFilter = useState<{ value: string, label: string }>('journal-filter-sort', () => JOURNAL_SORTING_OPTIONS[0])
  const selectedLegalArea = useState<{ value: string, label: string }[]>('journal-filter-legal-area', () => [])

  const searchLegalArea = ref<string>('')

  const { data, isLoading, suspense } = useSolrQueryV2(
    { [SolrParamsKey.ContentType]: ['journal'] },
    'journal',
    'journals',
  )

  const legalAreaOptions = computed(() => {
    const docs = (data.value?.response?.docs ?? []).filter(doc => doc.publisherId)
    const allLegalAreas = new Set<string>()
    
    for (const doc of docs) {
      for (const area of doc.legal_areas ?? []) {
        allLegalAreas.add(area)
      }
    }

    return Array.from(allLegalAreas)
    .map(area => ({ value: area, label: extractLabel(area) }))
    .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
  })

  const baseJournals = computed<JournalWithYearRange[]>(() => {
    const allJournals = data.value?.response?.docs ?? []

    const filteredJournals = allJournals.filter((journal) => {
      return matchesAllSearchTerms(searchKeyword.value, [journal.title || '', ...(journal.publisher || [])])
    })

    const groupedByPublisher = filteredJournals.reduce((acc, journal) => {
      const publisherId = journal.publisherId
      if (!publisherId) {
        return acc
      }

      if (!acc[publisherId]) {
        acc[publisherId] = []
      }
      acc[publisherId].push(journal)
      return acc
    }, {} as Record<string, JournalListItem[]>)

    const latestJournals = Object.values(groupedByPublisher).map((publisherJournals) => {
      const sortedJournals = publisherJournals.sort((a, b) => {
        const yearA = Number.parseInt(a.year || '0')
        const yearB = Number.parseInt(b.year || '0')
        return yearB - yearA
      })

      const latestJournal = sortedJournals[0]

      const currentYear = new Date().getFullYear()
      const years = publisherJournals
        .map(j => Number.parseInt(j.year || '0'))
        .filter(y => !Number.isNaN(y) && y > 0)
        .sort((a, b) => a - b) // ascending order

      const startYear = years.length > 0 ? years[0] : currentYear
      const endYear = years.length > 0 ? years[years.length - 1] : currentYear

      // Aggregate legal_areas from all issues, not just the latest
      const allLegalAreas = [...new Set(publisherJournals.flatMap(j => j.legal_areas ?? []))]

      return {
        ...latestJournal,
        legal_areas: allLegalAreas.length > 0 ? allLegalAreas : latestJournal?.legal_areas,
        yearRange: {
          start: startYear,
          end: endYear,
        },
      } as JournalWithYearRange
    })

    if (sortFilter.value.value === 'alfabetisk') {
      latestJournals.sort((a, b) => {
        const titleA = a.title || ''
        const titleB = b.title || ''
        return compareLocaleNorwegian(titleA, titleB)
      })
    }

    if (sortFilter.value.value === 'kronologisk') {
      latestJournals.sort((a, b) => {
        // Sort by end year first, then by start year if end years are equal
        if (b.yearRange.end !== a.yearRange.end) {
          return b.yearRange.end - a.yearRange.end
        }
        return b.yearRange.start - a.yearRange.start
      })
    }

    return latestJournals
  })

  const committedState = computed<JournalFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    sortFilter: sortFilter.value,
    selectedLegalArea: selectedLegalArea.value,
  }))

  const filteredJournals = computed(() => {
    const state = committedState.value
    return baseJournals.value.filter(journal => journalMatches(journal, state))
  })

  const countForFacet = createCountWithFilters(baseJournals, committedState, journalMatches)

  const { selected: legalAreaSelected, toggle: toggleLegalArea, facetOptions: legalAreaFacetOptions } = useFacetFilter(selectedLegalArea, legalAreaOptions, option => countForFacet({ selectedLegalArea: [option] }))

  const totalJournals = computed(() => filteredJournals.value.length)

  function countWithFilters(overrides: JournalDrawerOverrides): number {
    return countForFacet(overrides)
  }

  return {
    searchKeyword,
    sortFilter,
    filteredJournals,
    totalJournals,
    isLoading,
    suspense,
    legalAreaOptions,
    selectedLegalArea,
    countWithFilters,
    legalAreaSelected,
    toggleLegalArea,
    legalAreaFacetOptions,
    searchLegalArea,
  }
}

export const useJournalFilters = createSharedComposable(_useJournalFilters)
